import React, { useContext, useState } from 'react';
import { RegistrationContext } from '@/modules/RegisterLayout/contexts/RegistrationContextProvider';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Button, Typography, useToast } from '@pw-tech/omni-ui';
import { CourseContext } from '@/modules/RegisterLayout/contexts/CoursesContextProvider';
import { FileDown, InfoIcon } from 'lucide-react';
import FooterPortal from '@/modules/common/Portals/FooterPortal/FooterPortal';
import { getNextStep } from '@/modules/common/utils';
import { useDownloadFeeStructure } from '@/modules/RegisterLayout/hooks/useDownloadFeeStructure';
import { downloadPdfFromHtml } from '@/modules/RegisterLayout/utils/downloadPdfFromHtml';
const Pass = () => {
  const {
    registrationDetails,
    submitRegisterForm,
    isFormSubmitting
  } = useContext(RegistrationContext);
  const {
    selectedCourseData
  } = useContext(CourseContext);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const {
    refetch
  } = useDownloadFeeStructure();
  const {
    toast: showToaster
  } = useToast();
  const router = useRouter();
  const downloadFeeStructure = async () => {
    setIsDownloadLoading(true);
    try {
      const {
        data,
        isError
      } = await refetch();
      if (isError || !data?.html) {
        showToaster({
          message: 'Fee structure file is not available',
          variant: 'error',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center'
          }
        });
        return;
      }
      await downloadPdfFromHtml({
        html: data.html,
        style: data.style ?? ''
      });
    } catch {
      showToaster({
        message: 'Failed to get fee structure',
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        }
      });
    } finally {
      setIsDownloadLoading(false);
    }
  };
  return <article className="mt-6" data-sentry-component="Pass" data-sentry-source-file="Pass.tsx">
      <div className="bg-[#16B364] w-full px-5 rounded-t-lg flex gap-1 justify-center relative">
        <Image src="https://static.pw.live/5eb393ee95fab7468a79d189/ADMIN/31760585-0c93-4a59-ab8f-ea26c90f34e8.svg" alt="leafs" width={150} height={150} className="object-fill absolute left-4 sm:!left-16 top-[-20px] sm:!top-[-36px] w-[100px] h-[100px] sm:!w-[150px] sm:!h-[150px]" data-sentry-element="Image" data-sentry-source-file="Pass.tsx" />
        <Image src="https://static.pw.live/5eb393ee95fab7468a79d189/ADMIN/4d1d246d-71d6-4c17-a9a4-315013122196.svg" alt="trophy" width={150} height={150} data-sentry-element="Image" data-sentry-source-file="Pass.tsx" />
        <Image src="https://static.pw.live/5eb393ee95fab7468a79d189/ADMIN/31760585-0c93-4a59-ab8f-ea26c90f34e8.svg" alt="leafs" width={150} height={150} className="object-fill rotate-180 absolute right-4 sm:!right-16 top-[-20px] sm:!top-[-36px] w-[100px] h-[100px] sm:!w-[150px] sm:!h-[150px]" data-sentry-element="Image" data-sentry-source-file="Pass.tsx" />
      </div>
      <div className="relative flex p-5 bg-[#F6FEF9] rounded-lg gap-2 text-center items-center shadow-[0px_1px_8px_0px_rgba(0,0,0,0.08)]">
        <Image src="https://static.pw.live/5eb393ee95fab7468a79d189/ADMIN/11b0429b-6d1d-4ba4-9832-5afd5e2369f6.svg" alt="college" width={100} height={100} className="absolute bottom-0 w-[80px] h-[70px] sm:!w-[100px] sm:!h-[100px]" data-sentry-element="Image" data-sentry-source-file="Pass.tsx" />
        <div className="flex flex-col items-center self-center gap-2 mx-auto text-center">
          <Typography variant="regular" data-sentry-element="Typography" data-sentry-source-file="Pass.tsx">Congratulations! You are selected for</Typography>
          <Typography variant="heading3" weight="semi-bold" className="!text-[#099250]" data-sentry-element="Typography" data-sentry-source-file="Pass.tsx">
            {registrationDetails?.campusAlloted ?? ''}
          </Typography>
          <Typography variant="heading4" weight="semi-bold" data-sentry-element="Typography" data-sentry-source-file="Pass.tsx">
            for {selectedCourseData?.name ?? ''}
          </Typography>
          {registrationDetails?.scholarship ? <div className="border-2 border-dashed border-[#16B364] rounded-lg p-3 my-2 flex flex-col items-center">
              <Image src="https://static.pw.live/5eb393ee95fab7468a79d189/ADMIN/2d1a37d6-812d-4e70-8b14-03d497d42678.svg" alt="scholarship" width={40} height={40} className="" />
              <Typography variant="regular" className="!text-xs sm:!text-sm">
                Good news! You’ve earned a scholarship worth {registrationDetails?.scholarship ?? 0}
                %
              </Typography>
            </div> : null}
        </div>
        <Image src="https://static.pw.live/5eb393ee95fab7468a79d189/ADMIN/11b0429b-6d1d-4ba4-9832-5afd5e2369f6.svg" alt="college" width={100} height={100} className="absolute bottom-0 right-4 w-[80px] h-[70px] sm:!w-[100px] sm:!h-[100px]" data-sentry-element="Image" data-sentry-source-file="Pass.tsx" />
      </div>
      <div className="flex gap-1 my-4">
        <div className="flex flex-shrink-0 ">
          <InfoIcon size={20} data-sentry-element="InfoIcon" data-sentry-source-file="Pass.tsx" />
        </div>
        <Typography variant="regular" className="!text-sm !text-[#757575]" data-sentry-element="Typography" data-sentry-source-file="Pass.tsx">
          You will recieve a confirmation of the result on your registered email ID
        </Typography>
      </div>
      <div className="bg-[#F8F8F8] p-4 mt-4 rounded-lg flex flex-col sm:!flex-row gap-2 sm:!items-center">
        <Button variant="lowFocus" className="!text-[#196CDC] order-2 sm:!order-1 w-max" preIcon={<>
              <FileDown size={20} color="blue" />
            </>} onClick={() => downloadFeeStructure()} disabled={isDownloadLoading} data-sentry-element="Button" data-sentry-source-file="Pass.tsx">
          Fee Structure
        </Button>
        <Typography variant="regular" className="!text-[#3D3D3D] order-1 sm:!order-2" data-sentry-element="Typography" data-sentry-source-file="Pass.tsx">
          Please find fee structure after scholarship and pay admission fee!
        </Typography>
      </div>
      <div className="h-8" />
      <div className="py-2">
        <FooterPortal label="Continue" type="button" btnWrapperClass="relative sm:left-[31%]" onClick={() => {
        submitRegisterForm({
          step: getNextStep(router.query.step, registrationDetails?.step)
        }, true);
      }} isFormSubmitting={isFormSubmitting} data-sentry-element="FooterPortal" data-sentry-source-file="Pass.tsx" />
      </div>
    </article>;
};
export default Pass;